import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { RegisteredAssetModel } from '@casops/store/apis/registeredAssetsApi';
import {
  AssetDeviceTypeCellRenderer,
  assetDeviceTypeValueGetter,
} from '@casops/components/tables/cells/asset-device-type/AssetDeviceTypeCellRenderer';
import { AssetNameCellRenderer } from '@casops/components/tables/cells/asset-name/AssetNameCellRenderer';
import { getObjectTypeFromEnum } from '@casops/shared/utils/translationHelpers';
import { ObjectType } from '@casops/store/apis/commonModels';
import AssetGroupCellRenderer from '@casops/components/tables/cells/asset-group/AssetGroupCellRenderer';
import HealthStatusCellRenderer from '@casops/components/tables/cells/health-status/HealthStatusCellRenderer';
import ConfigurationGroupIdCellRenderer from '@casops/components/tables/cells/configuration-group/ConfigurationGroupIdCellRenderer';
import SoftwareStatusCellRenderer from '@casops/components/tables/cells/software-status/SoftwareStatusCellRenderer';
import GeofenceCountCellRenderer from '@casops/components/tables/cells/geofence-count/GeofenceCountCellRenderer';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useAssetGroupIdFormatter } from '@casops/components/tables/formatters/idFormatters';
import { useAbsoluteTimePartialColDef } from '@casops/components/tables/cells/absolute-time/useAbsoluteTime';

type Model = RegisteredAssetModel;

export function useAssetsColumnDefs(): ColDef<Model>[] {
  const { t } = useTranslation();

  const assetGroupIdFormatter = useAssetGroupIdFormatter();
  const absoluteTimeColDef = useAbsoluteTimePartialColDef<Model>();

  return useMemo<ColDef<Model>[]>(
    () => [
      {
        colId: 'AssetName',
        field: 'n',
        headerName: t('Fields.AssetName'),
        minWidth: 110,
        cellRenderer: AssetNameCellRenderer,
      },
      {
        colId: 'Timestamp',
        field: 'ts',
        headerName: t('Fields.Timestamp'),
        minWidth: 180,
        sort: 'desc',
        ...absoluteTimeColDef,
      },
      {
        colId: 'HealthStatus',
        field: 'stH',
        headerName: t('Fields.HealthStatus'),
        minWidth: 120,
        cellRenderer: HealthStatusCellRenderer,
      },
      {
        colId: 'SoftwareStatus',
        field: 'stC',
        headerName: t('Fields.SoftwareStatus'),
        minWidth: 130,
        cellRenderer: SoftwareStatusCellRenderer,
      },
      {
        colId: 'AssetGroup',
        field: 'ags',
        headerName: t('Fields.AssetGroup'),
        valueFormatter: assetGroupIdFormatter<Model, 'ags'>, // Searchable
        cellRenderer: AssetGroupCellRenderer,
        minWidth: 200,
      },
      {
        colId: 'ObjectType',
        field: 'obj',
        headerName: t('Fields.ObjectType'),
        minWidth: 150,
        valueGetter: ({ data }: ValueGetterParams<Model>): string =>
          getObjectTypeFromEnum(t, data?.obj ?? ObjectType.Unknown),
      },
      { colId: 'DeviceId', field: 'did', headerName: t('Fields.DeviceId'), minWidth: 150 },
      {
        colId: 'ConfigGroup',
        field: 'cfg',
        headerName: t('Fields.ConfigGroup'),
        minWidth: 250,
        cellRenderer: ConfigurationGroupIdCellRenderer,
      },
      {
        colId: 'Software',
        field: 'cas',
        headerName: t('Fields.Software'),
        minWidth: 160,
      },
      {
        colId: 'DeviceType',
        headerName: t('Fields.DeviceType'),
        minWidth: 110,
        valueGetter: assetDeviceTypeValueGetter,
        cellRenderer: AssetDeviceTypeCellRenderer,
      },
      {
        colId: 'GeofenceCount',
        field: 'totG',
        headerName: t('Fields.GeofenceCount'),
        minWidth: 130,
        cellRenderer: GeofenceCountCellRenderer,
      },
    ],
    [t, assetGroupIdFormatter, absoluteTimeColDef],
  );
}
